<template>
  <div class="attachment-preview">
    <div class="d-flex align-center ga-3">
      <v-icon :icon="kindIcon" color="primary" size="32"></v-icon>
      <div class="flex-grow-1" style="min-width: 0;">
        <div class="text-subtitle-2 font-weight-bold text-truncate" :title="attachment.filename">
          {{ total
              ? t('attachmentpreview.materialPosition', { position, total, name: attachment.filename })
              : attachment.filename }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ kindLabel }} · {{ t('attachmentpreview.pageCount', { count: attachment.page_count }, attachment.page_count) }}
        </div>
      </div>
    </div>

    <div v-if="isLoadingPages" class="d-flex align-center ga-2 mt-3 text-caption text-medium-emphasis">
      <v-progress-circular indeterminate size="16" width="2" color="primary"></v-progress-circular>
      {{ t('attachmentpreview.loadingPreview') }}
    </div>

    <div v-else-if="previewUnavailable" class="d-flex align-center ga-1 mt-3 text-caption text-medium-emphasis">
      <v-icon icon="mdi-image-off-outline" size="16"></v-icon>
      {{ previewError
          ? t('attachmentpreview.previewUnavailableWithReason', { reason: previewError })
          : t('attachmentpreview.previewUnavailable') }}
    </div>

    <div v-else-if="pages.length" class="attachment-pages mt-3">
      <a
          v-for="page in pages"
          :key="page.page"
          :href="page.url"
          target="_blank"
          rel="noopener noreferrer"
          class="attachment-page"
          :title="t('attachmentpreview.openPage', { page: page.page })"
      >
        <v-img :src="page.url" width="160" height="120" class="attachment-page-image rounded">
          <template v-slot:placeholder>
            <div class="d-flex align-center justify-center fill-height">
              <v-progress-circular indeterminate size="20" width="2" color="grey-lighten-1"></v-progress-circular>
            </div>
          </template>
          <template v-slot:error>
            <div class="d-flex align-center justify-center fill-height">
              <v-icon icon="mdi-image-broken-variant" color="grey"></v-icon>
            </div>
          </template>
        </v-img>
        <div class="text-caption text-center text-medium-emphasis mt-1">
          {{ t('attachmentpreview.page', { page: page.page }) }}
        </div>
      </a>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { get_file } from "@/api";
import { ATTACHMENT_KINDS, apiErrorDetail } from "@/utils/templateAttachment";

// One stimulus material of a focus-group result: name, kind, page count and a
// strip of page thumbnails. The result stores a reference without links (they
// would expire), so freshly signed ones are fetched whenever it is shown.
export default {
  name: 'AttachmentPreview',
  props: {
    // {file_id, filename, kind, mime_type, page_count}
    attachment: {
      type: Object,
      required: true,
    },
    // 1-based place in the run's order ("Material 2 of 3"), with `total`.
    position: {
      type: Number,
      default: null,
    },
    total: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const { t } = useI18n();

    return {
      t
    };
  },
  data() {
    return {
      pages: [],
      isLoadingPages: false,
      previewUnavailable: false,
      previewError: '',
      // Bumped per request and on unmount: a response for another file, or for
      // a preview no longer on screen, is dropped.
      requestToken: 0,
    };
  },
  computed: {
    kindIcon() {
      return ATTACHMENT_KINDS[this.attachment.kind]?.icon || 'mdi-file-outline';
    },
    kindLabel() {
      const labelKey = ATTACHMENT_KINDS[this.attachment.kind]?.labelKey;
      return labelKey ? this.t(labelKey) : (this.attachment.kind || '');
    },
  },
  watch: {
    'attachment.file_id': {
      immediate: true,
      handler() {
        this.loadPages();
      },
    },
  },
  beforeUnmount() {
    this.requestToken += 1;
  },
  methods: {
    // A failure (file no longer visible, storage disabled, ...) only costs this
    // file's thumbnails: its metadata stays and the rest of the page goes on.
    async loadPages() {
      const token = ++this.requestToken;
      this.pages = [];
      this.previewUnavailable = false;
      this.previewError = '';
      this.isLoadingPages = true;
      try {
        const response = await get_file(this.attachment.file_id);
        if (token !== this.requestToken) return;
        this.pages = Array.isArray(response.data?.pages) ? response.data.pages : [];
      } catch (error) {
        if (token !== this.requestToken) return;
        console.error("Error fetching attachment pages:", error);
        this.previewUnavailable = true;
        this.previewError = apiErrorDetail(error) || '';
      } finally {
        if (token === this.requestToken) {
          this.isLoadingPages = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.attachment-pages {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.attachment-page {
  flex: 0 0 auto;
  text-decoration: none;
}

.attachment-page-image {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
}
</style>
